import { useState } from 'react';

import {
  Assets,
  Button,
  ButtonTextOrIcon,
  Container,
  DataLoaderWithRetry,
  Feedback,
  Form,
  Headings,
} from '@foradevelopment/fora-ui';
import {
  EmploymentPeriodInput,
  EmploymentViewModelNew,
  FokParameterChoiceInput,
  UpdateEmploymentInput,
} from '@fora-monorepo/employer-my-pages-common/types';

import { useAppDispatch } from '../../redux/hooks';
import { updateEmployment } from '../../redux/employment/employmentThunks';
import { envConfig } from '../../config';
import { getParameterTKeys } from '../../common/utils';
import { EmploymentFormStepWrapper } from '../../components';
import { useTypedTranslation } from '../../i18n/i18n';
import { PensionParameterCheckbox } from '../../components/ManageEmployment/MarkupAndParametersForm/PensionParameterCheckbox';
import { useFetchParameterChoices } from '../../redux/employment/hooks/useFetchParameterChoices';

export const EditParameterForm = ({
  goToConfirmationPanel,
  employment,
  employmentPeriodIndex,
}: {
  goToConfirmationPanel: (employment: EmploymentViewModelNew) => void;
  employment: EmploymentViewModelNew;
  employmentPeriodIndex: number;
}) => {
  const { employmentPeriods } = employment;
  const { t } = useTypedTranslation();
  const dispatch = useAppDispatch();
  const [parameterChoicesInput, setParameterChoicesInput] = useState<
    { fokParameter: FokParameterChoiceInput; validateInput: () => boolean }[]
  >([]);
  const [errorSubmittingMessage, setErrorSubmittingMessage] = useState<string[]>([]);
  const [submittingForm, setSubmittingForm] = useState(false);

  const {
    isLoading,
    hasError,
    data: parameterChoices,
    retryRequest,
  } = useFetchParameterChoices(employment.policyNumber);

  const submitAddEmployment = async () => {
    setSubmittingForm(true);

    setErrorSubmittingMessage([]);

    const employmentPeriodInput: EmploymentPeriodInput = {
      index: employmentPeriodIndex,
      ...employmentPeriods[employmentPeriodIndex],
      fokParameterChoices: parameterChoicesInput.flatMap(p => p.fokParameter),
    };
    const employmentInputData: UpdateEmploymentInput = {
      partnerNumberIndividual: employment.employee.partnerNumberIndividual,
      employmentPeriod: employmentPeriodInput,
    };

    if (!employmentInputData.employmentPeriod?.fokParameterChoices.length) {
      setErrorSubmittingMessage([t('errorMessages.validation.parameter.makeparameterchoice')]);
      setSubmittingForm(false);
      return;
    }

    const result = await dispatch(
      updateEmployment({ unchangedEmployment: employment, updateEmploymentInput: employmentInputData })
    );
    if (updateEmployment.fulfilled.match(result)) {
      const updatedEmployment = result.payload;
      goToConfirmationPanel(updatedEmployment);
    }
    if (updateEmployment.rejected.match(result)) {
      setErrorSubmittingMessage(
        envConfig.devLogging && result.payload && result.payload.message
          ? [`Fel i backend: ${result.payload.message}`]
          : [t('errorMessages.contactSupport')]
      );
      setSubmittingForm(false);
    }
  };

  return (
    <Form preventDefault backgroundColor="transparent">
      <EmploymentFormStepWrapper>
        {isLoading || hasError ? (
          <DataLoaderWithRetry onClick={retryRequest} isLoading={isLoading} type="small"></DataLoaderWithRetry>
        ) : (
          parameterChoices &&
          parameterChoices.map((parameter, index) => (
            <Container key={index} marginTop={40} marginBottom={20}>
              <Headings.H5 marginBottom={6} color="black90">
                {t(getParameterTKeys(parameter.parameterValue).name)}
              </Headings.H5>
              <PensionParameterCheckbox
                fokParameter={parameter}
                setParameterChoicesInput={setParameterChoicesInput}
                employee={employment.employee}
                employmentStartDate={employmentPeriods[employmentPeriodIndex].employmentStartDate}
                isSetAlready={parameter.startDate !== null}
              ></PensionParameterCheckbox>
            </Container>
          ))
        )}
      </EmploymentFormStepWrapper>
      {!!errorSubmittingMessage.length && (
        <Feedback type="error" marginBottom={40} negativeSideMargins>
          {errorSubmittingMessage[0]}
        </Feedback>
      )}
      <Button type="button" onClick={submitAddEmployment} disabled={submittingForm}>
        <ButtonTextOrIcon
          icon={<Assets.Spinner color="white" width={20} center={false} />}
          textColor="white"
          isLoading={submittingForm}
        >
          {t('common.save')}
        </ButtonTextOrIcon>
      </Button>
    </Form>
  );
};
